import { cloneDeep } from 'lodash-es';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { creditConfig } from '../../constants/credit';
import {
  CreditApplicationStatus,
  CreditApplicationData,
  CreditFormSubVariant,
  CreditState,
  CreditStepKey,
  CreditSessionState,
} from '../../types/credit';

const initialCreditSessionState: CreditSessionState = {
  stepper: {
    companyDetails: {},
    billingDetails: {},
    insuranceDetails: {},
    review: {},
  },
};

const initialState: CreditState = {};

const creditSlice = createSlice({
  name: 'credit',
  initialState,
  reducers: {
    initCreditSessionState: (state, action: PayloadAction<{ sessionId: string }>) => {
      state[action.payload.sessionId] = initialCreditSessionState;
    },
    setCreditApplicationData: (
      state,
      action: PayloadAction<{
        sessionId: string;
        formData: CreditApplicationData;
        status?: CreditApplicationStatus;
      }>,
    ) => {
      const { sessionId, formData } = action.payload;
      const session = state[sessionId];
      if (!session) return;
      session.stepper.companyDetails.formData = formData.companyDetails;
      session.stepper.billingDetails.formData = formData.billingDetails;
      session.stepper.insuranceDetails.formData = formData.insuranceDetails;
      if (action.payload.status) {
        session.status = action.payload.status;
      }
    },
    setCreditApplicationStatus: (
      state,
      action: PayloadAction<{ sessionId: string; status: CreditApplicationStatus }>,
    ) => {
      const session = state[action.payload.sessionId];
      if (!session) return;
      session.status = action.payload.status;
    },
    setCreditApplicationStepData: (
      state,
      action: PayloadAction<{
        sessionId: string;
        stepKey: CreditStepKey;
        formData: Partial<CreditFormSubVariant>;
      }>,
    ) => {
      const session = state[action.payload.sessionId];
      if (!session) return;
      if (session.stepper[action.payload.stepKey]) {
        session.stepper[action.payload.stepKey].formData = cloneDeep(action.payload.formData);
        session.stepper[action.payload.stepKey].isComplete = true;
      }
    },
    setCreditApplicationStepper: (
      state,
      action: PayloadAction<{
        sessionId: string;
        status: CreditApplicationStatus;
      }>,
    ) => {
      const session = state[action.payload.sessionId];
      if (!session) return;
      const status = action.payload.status;

      session.stepper.companyDetails.isComplete =
        status === CreditApplicationStatus.STEP1_SAVED ||
        status === CreditApplicationStatus.STEP2_SAVED ||
        status === CreditApplicationStatus.STEP3_SAVED ||
        status === CreditApplicationStatus.SUBMITTED;

      session.stepper.billingDetails.isComplete =
        status === CreditApplicationStatus.STEP2_SAVED ||
        status === CreditApplicationStatus.STEP3_SAVED ||
        status === CreditApplicationStatus.SUBMITTED;

      session.stepper.insuranceDetails.isComplete =
        status === CreditApplicationStatus.STEP3_SAVED ||
        status === CreditApplicationStatus.SUBMITTED;

      session.stepper.review.isComplete = status === CreditApplicationStatus.SUBMITTED;
    },
    setCreditStepperProgress: (
      state,
      action: PayloadAction<{
        sessionId: string;
        stepKey: CreditStepKey;
      }>,
    ) => {
      const session = state[action.payload.sessionId];
      if (!session) return;
      const index = creditConfig.stepperOrder.findIndex((e) => e === action.payload.stepKey);
      creditConfig.stepperOrder.forEach((_e, i) => {
        if (i < index) {
          session.stepper[creditConfig.stepperOrder[i]].isComplete = true;
        } else {
          session.stepper[creditConfig.stepperOrder[i]].isComplete = false;
        }
      });
    },
    setCreditApplicationError: (
      state,
      action: PayloadAction<{ sessionId: string; error: string }>,
    ) => {
      const session = state[action.payload.sessionId];
      if (!session) return;
      session.error = action.payload.error;
    },
    clearCreditApplicationSession: (state, action: PayloadAction<{ sessionId: string }>) => {
      const session = state[action.payload.sessionId];
      if (!session) return;
      delete state[action.payload.sessionId];
    },
  },
});

export const {
  initCreditSessionState,
  setCreditApplicationData,
  setCreditApplicationStepData,
  setCreditApplicationStatus,
  setCreditApplicationStepper,
  setCreditStepperProgress,
  setCreditApplicationError,
  clearCreditApplicationSession,
} = creditSlice.actions;

export default creditSlice.reducer;
